import { useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Search, Trash2 } from 'lucide-react';
import { Button, ConfirmDialog, Input, ManagedDataTable, Select } from '@/design-system';
import { queryKeys } from '@/core/data-fetching/queryKeys';
import { useErrorToast } from '@/core/errors/useErrorToast';
import { useCompanyDetails } from '../company-details-context';
import { ph24LogsService, type Ph24Log, type Ph24LogDomain, type Ph24LogOrigin, type Ph24LogsFilters } from '../api/ph24-logs.service';

const PAGE_SIZE = 50;
const DOMAIN_LABELS: Record<Ph24LogDomain, string> = {
  unidade_medida: 'Unidade de Medida', forma_farmaceutica: 'Forma Farmacêutica', produto: 'Produto',
  sinonimo: 'Sinônimo', posologia: 'Posologia', capsula: 'Cápsula', produto_incompativel: 'Produto Incompatível',
  conselho_regional: 'Conselho Regional', orcamento: 'Orçamento',
};
const ORIGIN_LABELS: Record<Ph24LogOrigin, string> = { captura: 'Captura (FarmaFácil)', envio: 'Envio (PH24)' };
interface FormState { from: string; to: string; domain: string; origin: string; search: string }
const EMPTY_FORM: FormState = { from: '', to: '', domain: '', origin: '', search: '' };
function toFilters(companyId: string, form: FormState): Ph24LogsFilters {
  return {
    companyId,
    from: form.from ? new Date(`${form.from}T00:00:00`).toISOString() : undefined,
    to: form.to ? new Date(`${form.to}T23:59:59.999`).toISOString() : undefined,
    domain: (form.domain || undefined) as Ph24LogDomain | undefined,
    origin: (form.origin || undefined) as Ph24LogOrigin | undefined,
    search: form.search.trim() || undefined,
  };
}
const columns: ColumnDef<Ph24Log>[] = ([
  { accessorKey: 'createdAt', header: 'Data/Hora', cell: ({ row }) => new Date(row.original.createdAt).toLocaleString('pt-BR') },
  { accessorKey: 'domain', header: 'Módulo', cell: ({ row }) => DOMAIN_LABELS[row.original.domain] ?? row.original.domain },
  { accessorKey: 'origin', header: 'Origem', cell: ({ row }) => ORIGIN_LABELS[row.original.origin] ?? row.original.origin },
  { accessorKey: 'entityId', header: 'Entidade', cell: ({ row }) => row.original.entityId || '—' },
  { accessorKey: 'httpStatus', header: 'HTTP', cell: ({ row }) => row.original.httpStatus ?? '—' },
  { accessorKey: 'reason', header: 'Motivo', cell: ({ row }) => <span className="break-words">{row.original.reason}</span> },
] satisfies ColumnDef<Ph24Log>[]).map((column) => ({ ...column, enableSorting: false }));

export function CompanyDetailsPh24Tab() {
  const { company } = useCompanyDetails();
  return <CompanyPh24Logs key={company.id} companyId={company.id} />;
}

function CompanyPh24Logs({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [filters, setFilters] = useState<Ph24LogsFilters>(() => toFilters(companyId, EMPTY_FORM));
  const [page, setPage] = useState(1);
  // Capture the applied filters when opening confirmation, never a later draft.
  const [purgeTarget, setPurgeTarget] = useState<{ filters: Ph24LogsFilters; total: number } | null>(null);
  const logsQuery = useQuery({
    queryKey: queryKeys.ph24Logs.page({ ...filters, page }),
    queryFn: () => ph24LogsService.list({ ...filters, page, pageSize: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });
  const purgeMut = useMutation({
    mutationFn: () => {
      if (!purgeTarget) throw new Error('Confirme os filtros antes de limpar os logs.');
      return ph24LogsService.purge(purgeTarget.filters);
    },
    onSuccess: () => { setPurgeTarget(null); setPage(1); void queryClient.invalidateQueries({ queryKey: queryKeys.ph24Logs.all }); },
    onError: (error) => onError(error, 'Não foi possível limpar os logs PH24'),
  });
  const total = logsQuery.data?.total ?? 0;
  const update = (field: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm((previous) => ({ ...previous, [field]: event.target.value }));
  return <div className="space-y-4">
    <ManagedDataTable title="Logs PH24" subtitle="Registros do FarmaFácil que não foram capturados ou que a PH24 rejeitou na carga."
      columns={columns} data={logsQuery.data?.items ?? []} disableSearch loading={logsQuery.isFetching}
      emptyMessage="Nenhum log PH24 encontrado." defaultPageSize={PAGE_SIZE} pageSizeOptions={[PAGE_SIZE]}
      serverPagination={{ pageIndex: page - 1, pageSize: PAGE_SIZE, totalItems: total, onPageIndexChange: (index) => setPage(index + 1) }}
      toolbarButtons={[
        { label: 'Atualizar', icon: <RefreshCw />, variant: 'subtle', disabled: logsQuery.isFetching, onClick: () => { void logsQuery.refetch(); } },
        { label: 'Limpar logs', icon: <Trash2 />, variant: 'subtle', disabled: total === 0 || logsQuery.isFetching || logsQuery.isError || purgeMut.isPending, onClick: () => setPurgeTarget({ filters: { ...filters }, total }) },
      ]}
      customFilters={<form onSubmit={(event) => { event.preventDefault(); setPage(1); setFilters(toFilters(companyId, form)); }} className="flex w-full flex-wrap items-end gap-3">
        <div className="w-full sm:w-36"><Input label="De" type="date" value={form.from} onChange={update('from')} /></div>
        <div className="w-full sm:w-36"><Input label="Até" type="date" value={form.to} onChange={update('to')} /></div>
        <div className="w-full sm:w-48"><Select label="Módulo" aria-label="Módulo" value={form.domain} onChange={update('domain')}>
          <option value="">Todos os módulos</option>{Object.entries(DOMAIN_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </Select></div>
        <div className="w-full sm:w-44"><Select label="Origem" aria-label="Origem" value={form.origin} onChange={update('origin')}>
          <option value="">Todas as origens</option>{Object.entries(ORIGIN_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </Select></div>
        <div className="min-w-48 flex-1"><Input label="Buscar" placeholder="Código da entidade ou motivo…" value={form.search} onChange={update('search')} maxLength={120} /></div>
        <Button type="submit" size="sm" variant="subtle"><Search />Buscar</Button>
      </form>} />
    {logsQuery.isError && <p role="alert" className="text-sm text-red-600">Não foi possível carregar os logs PH24. Tente atualizar.</p>}
    <ConfirmDialog isOpen={!!purgeTarget} onClose={() => { if (!purgeMut.isPending) setPurgeTarget(null); }} onConfirm={() => purgeMut.mutate()}
      title="Limpar logs PH24" message={`Excluir os ${purgeTarget?.total ?? 0} log(s) que correspondem aos filtros aplicados? Essa ação não pode ser desfeita.`}
      confirmText="Excluir" variant="danger" isLoading={purgeMut.isPending} />
  </div>;
}
